import { extend } from 'vee-validate';
import { confirmed, email, min, required, max } from 'vee-validate/dist/rules';
import { useNuxt2App } from '~/composables/useNuxt2App';
import { z } from 'zod';

export function addMinRule() {
  const { i18n } = useNuxt2App();
  extend('min', {
    ...min,
    message: i18n.t('validator.min') as string,
  });
}
export function addMaxRule() {
  const { i18n } = useNuxt2App();
  extend('max', {
    ...max,
    message: i18n.t('validator.max') as string,
  });
}
export function addEmailRule() {
  const { i18n } = useNuxt2App();
  extend('email', {
    ...email,
    message: i18n.t('validator.email') as string,
  });
}
export function addRequiredRule() {
  const { i18n } = useNuxt2App();
  extend('required', {
    ...required,
    message: i18n.t('validator.empty') as string,
  });
}
export function addConfirmedRule() {
  const { i18n } = useNuxt2App();
  extend('confirmed', {
    ...confirmed,
    message: i18n.t('validator.confirmed') as string,
  });
}

export function addPhoneRule() {
  const { i18n } = useNuxt2App();
  extend('phone', {
    message: i18n.t('validator.phone') as string,
    validate(value) {
      return /^[+]?[0-9]{5,14}$/.test(value);
    },
  });
}

/**
 * Validates email addresses according to the following criteria:
 *
 * 1. `^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+`: Starts with one or more occurrences of alphanumeric characters (a-z, A-Z, 0-9) or special characters: .!#$%&'*+/=?^_`{|}~-. This covers the local part of the email address, i.e., the part before the "@" symbol. The "+" sign is also allowed for email addresses with tags.
 *
 * 2. `@`: There must be an "@" symbol separating the local part from the domain part of the email address.
 *
 * 3. `[a-zA-Z0-9]`: The domain must start with an alphanumeric character.
 *
 * 4. `(?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?`: The domain may then have up to 61 characters that can be alphanumeric or a hyphen ("-"), but it must not end with a hyphen. This part is optional to allow for short top-level domains (TLDs) like "google.com".
 *
 * 5. `(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*`: The part of the domain after the first dot. It must start with an alphanumeric character and can be followed by zero or more groups of up to 61 characters that can be alphanumeric or a hyphen, with each group preceded by a dot. This allows for validating multi-level domains, such as "subdomain.example.com".
 *
 * This regex does not validate the presence of Unicode or accented characters, which are allowed in the RFC specifications for email addresses, but may be restrictive for some real-world use cases. It is designed to fit the majority of traditional email address formats.
 */
export function validateEmailWithRegex(value: string): boolean {
  const emailSchema = z.string().email();
  return emailSchema.safeParse(value).success;
}
/**
 * Validates that the string contains no emojis or style characters.
 *
 * @param {string} value - The string to validate.
 * @returns {boolean} - Returns true if the string does not contain emojis or style characters.
 */
export function validateNoEmojisOrStyle(value) {
  const noEmojisOrStyleRegex = /[\uD800-\uDBFF][\uDC00-\uDFFF]/g;
  return !noEmojisOrStyleRegex.test(value);
}

/**
 * Validates that the name contains no special characters except spaces, - and '.
 *
 * @param {string} value - The name to validate.
 * @returns {boolean} - Returns true if the name does not contain special characters.
 */
export function validateNoSpecialCharsName(value) {
  const noSpecialCharsNameRegex = /[^\p{L}\s'´’-]/gu;
  return !noSpecialCharsNameRegex.test(value);
}

/**
 * Validates that the address contains no special characters except spaces ( . , ; ' “ & - )
 *
 * @param {string} value - The name to validate.
 * @returns {boolean} - Returns true if the name does not contain special characters.
 */
export function validateNoSpecialCharsAddress(value) {
  const noSpecialCharsAddressRegex = /[^ \p{L}\d.,;'´’"“&\-()/\\]/gu;
  return !noSpecialCharsAddressRegex.test(value);
}

/**
 * Regexps for validating zipcodes for different countries.
 * Based on https://github.com/validatorjs/validator.js/blob/master/src/lib/isPostalCode.js
 */
const threeDigit = /^\d{3}$/;
const fourDigit = /^\d{4}$/;
const fiveDigit = /^\d{5}$/;
const sixDigit = /^\d{6}$/;

const zipcodePatterns = {
  AD: /^AD\d{3}$/,
  AT: fourDigit,
  AU: fourDigit,
  AZ: /^AZ\d{4}$/,
  BA: /^([7-8]\d{4}$)/,
  BE: fourDigit,
  BG: fourDigit,
  BR: /^\d{5}-?\d{3}$/,
  BY: /^2[1-4]\d{4}$/,
  CA: /^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][\s\-]?\d[ABCEGHJ-NPRSTV-Z]\d$/i,
  CH: fourDigit,
  CN: /^(0[1-7]|1[012356]|2[0-7]|3[0-6]|4[0-7]|5[1-7]|6[1-7]|7[1-5]|8[1345]|9[09])\d{4}$/,
  CO: /^(05|08|11|13|15|17|18|19|20|23|25|27|41|44|47|50|52|54|63|66|68|70|73|76|81|85|86|88|91|94|95|97|99)(\d{4})$/,
  CZ: /^\d{3}\s?\d{2}$/,
  DE: fiveDigit,
  DK: fourDigit,
  DO: fiveDigit,
  DZ: fiveDigit,
  EE: fiveDigit,
  ES: /^(5[0-2]{1}|[0-4]{1}\d{1})\d{3}$/,
  FI: fiveDigit,
  FR: /^\d{2}\s?\d{3}$/,
  GB: /^(gir\s?0aa|[a-z]{1,2}\d[\da-z]?\s?(\d[a-z]{2})?)$/i,
  GR: /^\d{3}\s?\d{2}$/,
  HR: /^([1-5]\d{4}$)/,
  HT: /^HT\d{4}$/,
  HU: fourDigit,
  ID: fiveDigit,
  IE: /^(?!.*(?:o))[A-Za-z]\d[\dw]\s\w{4}$/i,
  IL: /^(\d{5}|\d{7})$/,
  IN: /^((?!10|29|35|54|55|65|66|86|87|88|89)[1-9][0-9]{5})$/,
  IR: /^(?!(\d)\1{3})[13-9]{4}[1346-9][013-9]{5}$/,
  IS: threeDigit,
  IT: fiveDigit,
  JP: /^\d{3}\-\d{4}$/,
  KE: fiveDigit,
  KR: /^(\d{5}|\d{6})$/,
  LI: /^(948[5-9]|949[0-7])$/,
  LT: /^LT\-\d{5}$/,
  LU: fourDigit,
  LV: /^LV\-\d{4}$/,
  LK: fiveDigit,
  MG: threeDigit,
  MX: fiveDigit,
  MT: /^[A-Za-z]{3}\s{0,1}\d{4}$/,
  MY: fiveDigit,
  NL: /^[1-9]\d{3}\s?(?!sa|sd|ss)[a-z]{2}$/i,
  NO: fourDigit,
  NP: /^(10|21|22|32|33|34|44|45|56|57)\d{3}$|^(977)$/i,
  NZ: fourDigit,
  PL: /^\d{2}\-\d{3}$/,
  PR: /^00[679]\d{2}([ -]\d{4})?$/,
  PT: /^\d{4}\-\d{3}?$/,
  RO: sixDigit,
  RU: sixDigit,
  SA: fiveDigit,
  SE: /^[1-9]\d{2}\s?\d{2}$/,
  SG: sixDigit,
  SI: fourDigit,
  SK: /^\d{3}\s?\d{2}$/,
  TH: fiveDigit,
  TN: fourDigit,
  TW: /^\d{3}(\d{2})?$/,
  UA: fiveDigit,
  US: /^\d{5}(-\d{4})?$/,
  ZA: fourDigit,
  ZM: fiveDigit,
};

/**
 * Validates that the zipcode is valid for the given country.
 *
 * @param value - zipcode to validate
 * @param country - country for which the zipcode is being validated
 * @returns - true if the zipcode is valid for the given country
 */
export function validateZipcode(value, country) {
  let pattern = zipcodePatterns[country];
  if (!pattern) {
    pattern = /^.{1,10}$/;
  }
  return pattern.test(value);
}

export function addNoSpecialCharsEmail() {
  const { i18n } = useNuxt2App();
  extend('noSpecialCharsEmail', {
    validate: (value) => validateEmailWithRegex(value),
    message: i18n.t('validator.emailSpecialChar') as string,
  });
}

export function addNoSpecialCharsName() {
  const { i18n } = useNuxt2App();
  extend('noSpecialCharsName', {
    validate: (value) => validateNoSpecialCharsName(value),
    message: i18n.t('validator.noSpecialCharsName') as string,
  });
}

export function addNoSpecialCharsAddress() {
  const { i18n } = useNuxt2App();
  extend('noSpecialCharsAddress', {
    validate: (value) => validateNoSpecialCharsAddress(value),
    message: i18n.t('validator.noSpecialCharsName') as string, // keep same label
  });
}

export function addNoEmojisOrStyle() {
  const { i18n } = useNuxt2App();
  extend('noEmojisOrStyle', {
    validate: (value) => validateNoEmojisOrStyle(value),
    message: i18n.t('validator.noEmojisOrStyle') as string,
  });
}

export function addZipcodeFranceMetropolitan(address) {
  const { i18n } = useNuxt2App();
  extend('zipcodeFranceMetropolitan', {
    validate(value) {
      const country = address.value.country ?? '';
      const zip = value ?? '';
      if (country === 'FR') {
        const zipNumber = parseInt(zip, 10);
        const isExcluded =
          (zipNumber >= 97000 && zipNumber <= 97999) ||
          (zipNumber >= 98100 && zipNumber <= 98999) ||
          [0, 99999].includes(zipNumber);
        return /^\d{5}$/.test(zip) && !isExcluded;
      }
      return true;
    },
    message: i18n.t('address-form.errors.postalCodeFranceMetropolitan') as string,
  });
}

export function addZipcode(address) {
  const { i18n } = useNuxt2App();
  extend('zipcode', {
    message: i18n.t('address-form.errors.postalCode') as string,
    validate(value) {
      return validateZipcode(value, address.value.country);
    },
  });
}
